<script>
  /**
   * Alert Component - Display informational messages with different severities
   * @component
   */
  import Icon from "./Icon.svelte";

  let {
    type = "info",
    title = "",
    message = "",
    icon = "",
    dismissable = false,
    onDismiss = () => {},
  } = $props();

  let dismissed = $state(false);

  const defaultIcons = {
    info: "Info",
    warning: "AlertTriangle",
    error: "AlertCircle",
    success: "CheckCircle2",
  };

  // Use custom icon if provided, otherwise use default based on type
  const displayIcon = $derived(icon || defaultIcons[type]);

  const colorClasses = {
    info: "alert-info",
    warning: "alert-warning",
    error: "alert-error",
    success: "alert-success",
  };

  function handleDismiss() {
    dismissed = true;
    onDismiss();
  }
</script>

{#if !dismissed}
  <div class="alert {colorClasses[type]}" role="alert">
    <div class="alert-content">
      <div class="alert-icon">
        <Icon name={displayIcon} size={20} />
      </div>
      <div class="alert-body">
        {#if title}
          <div class="alert-title">{title}</div>
        {/if}
        <div class="alert-message">{message}</div>
      </div>
    </div>
    {#if dismissable}
      <button
        class="alert-dismiss"
        type="button"
        onclick={handleDismiss}
        aria-label="Dismiss alert"
      >
        ×
      </button>
    {/if}
  </div>
{/if}

<style>
  .alert {
    display: flex;
    align-items: flex-start;
    gap: var(--space-3);
    padding: var(--space-4);
    border-radius: var(--radius-lg);
    border: 1px solid;
    font-size: var(--text-sm);
    line-height: 1.5;
    margin-bottom: var(--space-4);
  }

  .alert-content {
    display: flex;
    align-items: flex-start;
    gap: var(--space-3);
    flex: 1;
  }

  .alert-icon {
    font-size: 20px;
    flex-shrink: 0;
    line-height: 1;
  }

  .alert-body {
    flex: 1;
  }

  .alert-title {
    font-weight: var(--font-semibold);
    margin-bottom: var(--space-1);
    color: inherit;
  }

  .alert-message {
    color: inherit;
    opacity: 0.95;
    white-space: pre-line;
  }

  .alert-dismiss {
    background: none;
    border: none;
    font-size: 24px;
    line-height: 1;
    cursor: pointer;
    padding: 0;
    margin-left: var(--space-2);
    opacity: 0.6;
    transition: opacity 0.2s;
    color: inherit;
  }

  .alert-dismiss:hover {
    opacity: 1;
  }

  /* Info variant */
  .alert-info {
    background: #eff6ff;
    border-color: #3b82f6;
    color: #1e40af;
  }

  /* Warning variant */
  .alert-warning {
    background: #fffbeb;
    border-color: #f59e0b;
    color: #92400e;
  }

  /* Error variant */
  .alert-error {
    background: #fef2f2;
    border-color: #ef4444;
    color: #991b1b;
  }

  /* Success variant */
  .alert-success {
    background: #f0fdf4;
    border-color: #22c55e;
    color: #166534;
  }
</style>
